'use client';

import LottieComponent from '@/components/ui/card-with-image/lottie-component';
import RiveComponent from '@/components/ui/card-with-image/rive-component';
import HtmlEmbed from '@/components/ui/media/html-embed-client';
import { cn } from '@/lib/utils';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { LogoItem } from './scrolling-showcase';
import styles from './scrolling-ticker.module.scss';

export type ScrollingTickerProps = {
  logos: LogoItem[];
  logosPerSlide?: number;
  mobileLogosPerSlide?: number;
  logoDisplay?: 'circular' | 'natural';
  enableAutoscroll?: boolean;
  scrollSpeed?: number; // seconds between auto-advances, default 3
  isBackgroundDark?: boolean;
  centerDotsOnMobile?: boolean;
  logoMaxHeight?: number;
};

export const ScrollingTicker = ({
  logos,
  logosPerSlide,
  mobileLogosPerSlide,
  logoDisplay = 'circular',
  enableAutoscroll = false,
  scrollSpeed = 3,
  isBackgroundDark = false,
  centerDotsOnMobile = false,
  logoMaxHeight,
}: ScrollingTickerProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoscrollMs = scrollSpeed * 1000;

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: true,
    renderMode: 'performance',
    drag: true,
    slides: (containerWidth: number) => {
      const isMobile = containerWidth < 792;
      const perSlide = isMobile && mobileLogosPerSlide ? mobileLogosPerSlide : logosPerSlide;

      if (perSlide) {
        const gap = 20;
        const totalGap = gap * (perSlide - 1);
        const slideWidth = (containerWidth - totalGap) / perSlide;
        return logos.map(() => ({
          size: slideWidth / containerWidth,
          spacing: gap / containerWidth,
        }));
      }
      const slideWidth = containerWidth >= 1024 ? 166 : 116;
      const gap = 20;
      return logos.map(() => ({
        size: slideWidth / containerWidth,
        spacing: gap / containerWidth,
      }));
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
      setProgressKey((k) => k + 1);
    },
  });

  // Autoscroll driven by a simple interval
  useEffect(() => {
    if (!enableAutoscroll || isPaused) return;

    timerRef.current = setInterval(() => {
      instanceRef.current?.next();
    }, autoscrollMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enableAutoscroll, isPaused, autoscrollMs, instanceRef]);

  const handleMouseEnter = () => {
    if (enableAutoscroll) setIsPaused(true);
  };

  const handleMouseLeave = () => {
    if (enableAutoscroll) {
      setIsPaused(false);
      setProgressKey((k) => k + 1);
    }
  };

  const goPrev = () => {
    instanceRef.current?.prev();
    if (enableAutoscroll) {
      setIsPaused(false);
      setProgressKey((k) => k + 1);
    }
  };

  const goNext = () => {
    instanceRef.current?.next();
    if (enableAutoscroll) {
      setIsPaused(false);
      setProgressKey((k) => k + 1);
    }
  };

  const renderLogoMedia = (logo: LogoItem) => {
    if (logo.embedCode) {
      return <HtmlEmbed code={logo.embedCode} />;
    }
    if (logo.videoEmbedUrl) {
      return (
        <iframe
          src={logo.videoEmbedUrl}
          title={logo.alt}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }
    if (logo.lottieJson) {
      return <LottieComponent data={logo.lottieJson} className="h-full w-full" />;
    }
    if (logo.animationSrc && logo.animationFormat === 'Lottie') {
      return <LottieComponent src={logo.animationSrc} className="h-full w-full" />;
    }
    if (logo.animationSrc && logo.animationFormat === 'Rive') {
      return <RiveComponent src={logo.animationSrc} className="h-full w-full" />;
    }
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={logo.src} alt={logo.alt} />;
  };

  if (!logos.length) return null;

  return (
    <div className={styles.outer}>
      {/* Slider row */}
      <div className={styles.wrapper}>
        <button onClick={goPrev} className={styles.arrow} aria-label="Previous logos">
          <ArrowRight width={24} height={24} className={styles.arrow__icon_prev} />
        </button>

        <div
          className={styles.track_wrapper}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div ref={sliderRef} className={cn('keen-slider', styles.track)}>
            {logos.map((logo, i) => (
              <div
                key={i}
                className={cn(
                  'keen-slider__slide',
                  styles.logo_item,
                  styles[`logo_item--${logoDisplay}`],
                )}
              >
                <div
                  className={styles.logo_inner}
                  style={
                    logoMaxHeight
                      ? ({ '--logo-max-height': `${logoMaxHeight}px` } as React.CSSProperties)
                      : undefined
                  }
                >
                  {(() => {
                    const media = renderLogoMedia(logo);
                    if (logo.url) {
                      return (
                        <a href={logo.url} target="_blank" rel="noopener noreferrer">
                          <div className={styles.logo_img} draggable={false}>
                            {media}
                          </div>
                        </a>
                      );
                    }
                    return (
                      <div>
                        <div className={styles.logo_img} draggable={false}>
                          {media}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button onClick={goNext} className={styles.arrow} aria-label="Next logos">
          <ArrowRight width={24} height={24} />
        </button>
      </div>

      {/* Pagination dots */}
      <div className={cn(styles.dots, centerDotsOnMobile && styles['dots--center-mobile'])}>
        {logos.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              instanceRef.current?.moveToIdx(i);
              if (enableAutoscroll) {
                setIsPaused(false);
                setProgressKey((k) => k + 1);
              }
            }}
            className={cn(
              styles.dot,
              i === currentSlide && styles['dot--active'],
              i === currentSlide && enableAutoscroll && styles['dot--autoscroll'],
              isBackgroundDark && styles['dot--dark'],
            )}
            aria-label={`Go to logo ${i + 1}`}
          >
            {i === currentSlide && enableAutoscroll && (
              <span
                key={progressKey}
                className={cn(
                  styles.dot__progress,
                  isBackgroundDark && styles['dot__progress--dark'],
                )}
                style={
                  {
                    '--progress-duration': `${autoscrollMs}ms`,
                    animationPlayState: isPaused ? 'paused' : 'running',
                  } as React.CSSProperties
                }
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
